/**
 * MetaMask utility functions for wallet connection and management
 */

import { NetworkParams } from '@/types';
import { ethers } from 'ethers';
import { SWM_ABI } from '@/abi/SWM';
import {
  TOKEN_CONFIGS,
  swmAddress,
  wethAddress,
  hfswmAddress,
  llmnftAddress,
  chainId as targetChainId,
} from '@/constants';

// Note: Window.ethereum is declared in react-app-env.d.ts

// Store wrapped event handlers for proper cleanup
const eventHandlerMap = new WeakMap<Function, (...args: unknown[]) => void>();

/**
 * MetaMask detection utility function to check if extension is installed
 * @returns boolean indicating if MetaMask is available
 */
export const checkMetaMaskAvailability = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof window.ethereum !== 'undefined' &&
    Boolean(window.ethereum.isMetaMask)
  );
};

/**
 * Request wallet connection from MetaMask
 * @returns Promise<string[]> array of connected account addresses
 * @throws Error if connection fails or is rejected
 */
export const requestWalletConnection = async (): Promise<string[]> => {
  if (!window.ethereum) {
    throw new Error('MetaMask is not installed');
  }

  try {
    const accounts = (await window.ethereum.request({
      method: 'eth_requestAccounts',
    })) as string[];

    if (!accounts || accounts.length === 0) {
      throw new Error(
        'No accounts found. Please make sure MetaMask is unlocked.'
      );
    }

    return accounts;
  } catch (error) {
    // Re-throw with more specific error messages
    if (error instanceof Error) {
      if (error.message.includes('User rejected')) {
        throw new Error(
          'Connection request was rejected. Please approve the connection in MetaMask.'
        );
      }
      if (error.message.includes('User denied')) {
        throw new Error(
          'Connection request was rejected. Please approve the connection in MetaMask.'
        );
      }
    }

    // Check for error codes
    if ((error as any).code === 4001) {
      throw new Error(
        'Connection request was rejected. Please approve the connection in MetaMask.'
      );
    }

    throw error;
  }
};

/**
 * Get currently connected accounts from MetaMask
 * @returns Promise<string[]> array of connected account addresses
 */
export const getConnectedAccounts = async (): Promise<string[]> => {
  if (!window.ethereum) {
    return [];
  }

  try {
    const accounts = (await window.ethereum.request({
      method: 'eth_accounts',
    })) as string[];

    return accounts || [];
  } catch (error) {
    console.error('Failed to get connected accounts:', error);
    return [];
  }
};

/**
 * Check if MetaMask is connected to any accounts
 * @returns Promise<boolean> true if connected to at least one account
 */
export const isMetaMaskConnected = async (): Promise<boolean> => {
  const accounts = await getConnectedAccounts();
  return accounts.length > 0;
};

/**
 * Format wallet address for display (truncated format)
 * @param address - Full wallet address
 * @param startChars - Number of characters to show at start (default: 4)
 * @param endChars - Number of characters to show at end (default: 4)
 * @returns Formatted address string (e.g., "0x7eE7...9F27")
 */
export const formatWalletAddress = (
  address: string,
  startChars: number = 4,
  endChars: number = 4
): string => {
  if (!address || address.length < startChars + endChars) {
    return address;
  }

  return `${address.slice(0, startChars)}...${address.slice(-endChars)}`;
};

/**
 * Add event listeners for MetaMask events
 * @param onAccountsChanged - Callback for account changes
 * @param onChainChanged - Callback for chain changes
 * @param onConnect - Callback for connection events
 * @param onDisconnect - Callback for disconnection events
 */
export const addMetaMaskEventListeners = (
  onAccountsChanged?: (accounts: string[]) => void,
  onChainChanged?: (chainId: string) => void,
  onConnect?: (connectInfo: { chainId: string }) => void,
  onDisconnect?: (error: { code: number; message: string }) => void
): void => {
  if (!window.ethereum) {
    return;
  }

  if (onAccountsChanged) {
    const wrappedHandler = (...args: unknown[]) => {
      onAccountsChanged(args[0] as string[]);
    };
    eventHandlerMap.set(onAccountsChanged, wrappedHandler);
    window.ethereum.on('accountsChanged', wrappedHandler);
  }

  if (onChainChanged) {
    const wrappedHandler = (...args: unknown[]) => {
      onChainChanged(args[0] as string);
    };
    eventHandlerMap.set(onChainChanged, wrappedHandler);
    window.ethereum.on('chainChanged', wrappedHandler);
  }

  if (onConnect) {
    const wrappedHandler = (...args: unknown[]) => {
      onConnect(args[0] as { chainId: string });
    };
    eventHandlerMap.set(onConnect, wrappedHandler);
    window.ethereum.on('connect', wrappedHandler);
  }

  if (onDisconnect) {
    const wrappedHandler = (...args: unknown[]) => {
      onDisconnect(args[0] as { code: number; message: string });
    };
    eventHandlerMap.set(onDisconnect, wrappedHandler);
    window.ethereum.on('disconnect', wrappedHandler);
  }
};

/**
 * Remove event listeners for MetaMask events
 * @param onAccountsChanged - Callback for account changes
 * @param onChainChanged - Callback for chain changes
 * @param onConnect - Callback for connection events
 * @param onDisconnect - Callback for disconnection events
 */
export const removeMetaMaskEventListeners = (
  onAccountsChanged?: (accounts: string[]) => void,
  onChainChanged?: (chainId: string) => void,
  onConnect?: (connectInfo: { chainId: string }) => void,
  onDisconnect?: (error: { code: number; message: string }) => void
): void => {
  if (!window.ethereum) {
    return;
  }

  if (onAccountsChanged) {
    const wrappedHandler = eventHandlerMap.get(onAccountsChanged);
    if (wrappedHandler) {
      window.ethereum.removeListener('accountsChanged', wrappedHandler);
      eventHandlerMap.delete(onAccountsChanged);
    }
  }

  if (onChainChanged) {
    const wrappedHandler = eventHandlerMap.get(onChainChanged);
    if (wrappedHandler) {
      window.ethereum.removeListener('chainChanged', wrappedHandler);
      eventHandlerMap.delete(onChainChanged);
    }
  }

  if (onConnect) {
    const wrappedHandler = eventHandlerMap.get(onConnect);
    if (wrappedHandler) {
      window.ethereum.removeListener('connect', wrappedHandler);
      eventHandlerMap.delete(onConnect);
    }
  }

  if (onDisconnect) {
    const wrappedHandler = eventHandlerMap.get(onDisconnect);
    if (wrappedHandler) {
      window.ethereum.removeListener('disconnect', wrappedHandler);
      eventHandlerMap.delete(onDisconnect);
    }
  }
};

/**
 * Error types for MetaMask operations
 */
export enum MetaMaskErrorType {
  NOT_INSTALLED = 'NOT_INSTALLED',
  USER_REJECTED = 'USER_REJECTED',
  NO_ACCOUNTS = 'NO_ACCOUNTS',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * MetaMask error class with specific error types
 */
export class MetaMaskError extends Error {
  public readonly type: MetaMaskErrorType;
  public readonly code?: number;

  constructor(type: MetaMaskErrorType, message: string, code?: number) {
    super(message);
    this.name = 'MetaMaskError';
    this.type = type;
    this.code = code;
  }
}

/**
 * Parse MetaMask errors and return user-friendly error messages
 * @param error - The error to parse
 * @returns MetaMaskError with appropriate type and message
 */
export const parseMetaMaskError = (error: unknown): MetaMaskError => {
  if (error instanceof MetaMaskError) {
    return error;
  }

  if (error instanceof Error) {
    // Check for specific error patterns
    if (
      error.message.includes('User rejected') ||
      error.message.includes('User denied')
    ) {
      return new MetaMaskError(
        MetaMaskErrorType.USER_REJECTED,
        'Connection request was rejected. Please approve the connection in MetaMask.',
        (error as any).code
      );
    }

    if (error.message.includes('No accounts')) {
      return new MetaMaskError(
        MetaMaskErrorType.NO_ACCOUNTS,
        'No accounts found. Please make sure MetaMask is unlocked and has at least one account.',
        (error as any).code
      );
    }
  }

  // Check for error codes (handle non-Error objects with codes)
  if ((error as any)?.code === 4001) {
    return new MetaMaskError(
      MetaMaskErrorType.USER_REJECTED,
      'Connection request was rejected. Please approve the connection in MetaMask.',
      4001
    );
  }

  if ((error as any)?.code === -32002) {
    return new MetaMaskError(
      MetaMaskErrorType.NETWORK_ERROR,
      'MetaMask is already processing a request. Please wait and try again.',
      -32002
    );
  }

  // Default to unknown error
  return new MetaMaskError(
    MetaMaskErrorType.UNKNOWN_ERROR,
    'An unexpected error occurred while connecting to MetaMask. Please try again.',
    (error as any)?.code
  );
};

/**
 * Get user-friendly error message for MetaMask errors
 * @param error - MetaMask error or unknown error
 * @returns User-friendly error message with retry guidance
 */
export const getMetaMaskErrorMessage = (error: unknown): string => {
  const metaMaskError = parseMetaMaskError(error);

  switch (metaMaskError.type) {
    case MetaMaskErrorType.NOT_INSTALLED:
      return 'MetaMask is not installed. Please install MetaMask extension and refresh the page.';

    case MetaMaskErrorType.USER_REJECTED:
      return 'Connection request was rejected. Please click "Connect Wallet" and approve the connection in MetaMask.';

    case MetaMaskErrorType.NO_ACCOUNTS:
      return 'No accounts found. Please make sure MetaMask is unlocked and has at least one account.';

    case MetaMaskErrorType.NETWORK_ERROR:
      return 'MetaMask is busy processing another request. Please wait a moment and try again.';

    case MetaMaskErrorType.UNKNOWN_ERROR:
    default:
      return 'Failed to connect to MetaMask. Please make sure MetaMask is installed and try again.';
  }
};

/**
 * Create an ethers provider from MetaMask
 * @returns ethers.BrowserProvider instance or null if MetaMask not available
 */
export const createMetaMaskProvider = (): ethers.BrowserProvider | null => {
  if (!checkMetaMaskAvailability()) {
    return null;
  }

  try {
    return new ethers.BrowserProvider(window.ethereum!);
  } catch (error) {
    console.error('Failed to create MetaMask provider:', error);
    return null;
  }
};

/**
 * Get signer from MetaMask provider
 * @returns Promise<ethers.JsonRpcSigner> or throws error
 */
export const getMetaMaskSigner = async (): Promise<ethers.JsonRpcSigner> => {
  const provider = createMetaMaskProvider();
  if (!provider) {
    throw new MetaMaskError(
      MetaMaskErrorType.NOT_INSTALLED,
      'MetaMask is not installed or not available'
    );
  }

  try {
    return await provider.getSigner();
  } catch (error) {
    throw parseMetaMaskError(error);
  }
};

/**
 * Switch to a specific network in MetaMask
 * @param networkParams - Network configuration parameters
 * @returns Promise<void>
 */
export const switchMetaMaskNetwork = async (
  networkParams: NetworkParams
): Promise<void> => {
  if (!window.ethereum) {
    throw new MetaMaskError(
      MetaMaskErrorType.NOT_INSTALLED,
      'MetaMask is not installed'
    );
  }

  try {
    // Try to switch to the network first
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: networkParams.chainId }],
    });
  } catch (switchError: any) {
    // If the network doesn't exist, add it
    if (switchError.code === 4902) {
      try {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [networkParams],
        });
      } catch (addError) {
        throw parseMetaMaskError(addError);
      }
    } else {
      throw parseMetaMaskError(switchError);
    }
  }
};

/**
 * Get current network information from MetaMask
 * @returns Promise<{chainId: string, networkVersion: string}>
 */
export const getCurrentNetwork = async (): Promise<{
  chainId: string;
  networkVersion: string;
}> => {
  if (!window.ethereum) {
    throw new MetaMaskError(
      MetaMaskErrorType.NOT_INSTALLED,
      'MetaMask is not installed'
    );
  }

  try {
    const chainId = (await window.ethereum.request({
      method: 'eth_chainId',
    })) as string;

    const networkVersion = (await window.ethereum.request({
      method: 'net_version',
    })) as string;

    return { chainId, networkVersion };
  } catch (error) {
    throw parseMetaMaskError(error);
  }
};

/**
 * Connect wallet and switch to specific network (combines connection + network switching)
 * @param networkParams - Optional network to switch to after connection
 * @returns Promise<string[]> array of connected accounts
 */
export const connectWalletAndSwitchNetwork = async (
  networkParams?: NetworkParams
): Promise<string[]> => {
  // First connect the wallet
  const accounts = await requestWalletConnection();

  // Then switch network if provided
  if (networkParams) {
    await switchMetaMaskNetwork(networkParams);
  }

  return accounts;
};

/**
 * Request account access (for backwards compatibility with old Deposit component)
 * @returns Promise<string[]> array of connected accounts
 */
export const requestAccountAccess = async (): Promise<string[]> => {
  return await requestWalletConnection();
};

/**
 * Add an ERC-20 token to MetaMask via wallet_watchAsset
 */
export const addERC20Token = async (
  symbol: 'SWM' | 'WETH' | 'HFSWM',
  imageUrl?: string
): Promise<boolean> => {
  if (!window.ethereum) {
    throw new MetaMaskError(
      MetaMaskErrorType.NOT_INSTALLED,
      'MetaMask is not installed'
    );
  }

  const tokenConfig = TOKEN_CONFIGS[symbol];
  const addressMap = {
    SWM: swmAddress,
    WETH: wethAddress,
    HFSWM: hfswmAddress,
  } as const;

  try {
    // Attempt to read decimals from the on-chain contract to avoid mismatch errors
    let decimals: number = Number(tokenConfig.decimals);
    try {
      const provider = new ethers.BrowserProvider(window.ethereum as any);
      const erc20 = new ethers.Contract(addressMap[symbol], SWM_ABI, provider);
      const onChainDecimals = await erc20.decimals();
      if (typeof onChainDecimals === 'number') {
        decimals = onChainDecimals as number;
      } else if (
        onChainDecimals &&
        typeof onChainDecimals.toString === 'function'
      ) {
        decimals = Number(onChainDecimals.toString());
      }
    } catch (readErr) {
      // Fall back to config decimals if read fails
    }

    const added = await (window.ethereum as any).request({
      method: 'wallet_watchAsset',
      params: {
        type: 'ERC20',
        options: {
          address: addressMap[symbol],
          symbol: tokenConfig.symbol,
          decimals,
          image: imageUrl,
        },
      },
    });
    return Boolean(added);
  } catch (error) {
    throw parseMetaMaskError(error);
  }
};

/**
 * Add all supported ERC-20 tokens (SWM, WETH, HFSWM). Ignores individual failures.
 */
export const addAllSupportedERC20Tokens = async (): Promise<void> => {
  const tokens: Array<'SWM' | 'WETH' | 'HFSWM'> = ['SWM', 'WETH', 'HFSWM'];
  for (const t of tokens) {
    try {
      await addERC20Token(t);
    } catch {
      // ignore single token failure
    }
  }
};

/**
 * Add LLMNFT by tokenId to MetaMask (ERC721)
 */
export const addLLMNFT = async (tokenId: string | number): Promise<boolean> => {
  if (!window.ethereum) {
    throw new MetaMaskError(
      MetaMaskErrorType.NOT_INSTALLED,
      'MetaMask is not installed'
    );
  }

  try {
    const added = await (window.ethereum as any).request({
      method: 'wallet_watchAsset',
      params: {
        type: 'ERC721',
        options: {
          address: llmnftAddress,
          tokenId: String(tokenId),
        },
      },
    });
    return Boolean(added);
  } catch (error) {
    throw parseMetaMaskError(error);
  }
};

/**
 * Attempt to add SWM token once per target chain. Non-fatal; returns boolean.
 */
export const ensureSWMTokenWatchedOnce = async (): Promise<boolean> => {
  try {
    const tokenAddedKey = `mm_added_swm_${targetChainId}`;
    if (!localStorage.getItem(tokenAddedKey)) {
      const added = await addERC20Token('SWM');
      if (added) {
        localStorage.setItem(tokenAddedKey, '1');
      }
      return added;
    }
    return true;
  } catch (err) {
    console.warn('Adding SWM token to MetaMask failed (non-fatal):', err);
    return false;
  }
};
